import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { TabsProps } from '@atlaskit/tabs';
import {
	useAdjustmentsEnabled,
	useAdjustedScreenTabs,
	useTriggerFieldInitialization,
} from '@atlassian/jira-issue-adjustments';
import type { HookResult, Props, ScreenTabCommonProps } from './types';

export const useUimScreenTabs = <ScreenTabProps extends ScreenTabCommonProps>({
	formState,
	tabs,
	selected,
	onChange,
}: Props<ScreenTabProps>): HookResult<ScreenTabProps> => {
	const adjustmentsAreEnabled = useAdjustmentsEnabled();
	const [adjustedScreenTabs, { initializeScreenTabs, updateActiveScreenTab }] =
		useAdjustedScreenTabs();
	const [triggerFieldInitializationKey] = useTriggerFieldInitialization();
	const selectedRef = useRef(selected);

	// Always refreshes `selectedRef` value using `selected` prop value
	selectedRef.current = selected;

	// Initialize screen tabs (or re-initialize when deps change) in UIM store
	useEffect(() => {
		if (adjustmentsAreEnabled) {
			if (tabs.length === 1) {
				// receiving single tab means there's no screen tab visible on view
				initializeScreenTabs({ tabs: [], selected: 0 });
			} else {
				// receiving undefined selected props means we whould default to the first tab
				initializeScreenTabs({ tabs, selected: selectedRef.current ?? 0 });
			}
		}

		/**
		 * Because of how the GIC works between subsequent modal openings, UI modifications state is persisted.
		 * That's why want to run initializeScreenTabs when triggerFieldInitializationKey changes.
		 */
	}, [adjustmentsAreEnabled, initializeScreenTabs, tabs, triggerFieldInitializationKey]);

	// Controlled tabs are synced with UIM store
	const controlledTabs = useMemo(() => {
		if (adjustmentsAreEnabled && Array.isArray(adjustedScreenTabs)) {
			const adjustedVisibilityMap: Record<string, boolean> = {};
			for (const adjustedScreenTab of adjustedScreenTabs) {
				adjustedVisibilityMap[adjustedScreenTab.id] = adjustedScreenTab.isVisible;
			}

			return tabs.map((inputTab) => ({
				...inputTab,
				// isVisible is determined by the UIM store, but if not, default to true
				isVisible: adjustedVisibilityMap[inputTab.id] ?? true,
			}));
		}

		// default all tabs to be visible
		return tabs.map((inputTab) => ({
			...inputTab,
			isVisible: true,
		}));
	}, [adjustmentsAreEnabled, adjustedScreenTabs, tabs]);

	// Controlled selected is synced with UIM store unless there are errors to fix on the form
	const controlledSelected = useMemo(() => {
		const shouldHighlightFormError = formState.hasValidationErrors && formState.submitFailed;
		if (!adjustmentsAreEnabled || !Array.isArray(adjustedScreenTabs) || shouldHighlightFormError) {
			return selected;
		}

		const activeAdjustedTab = adjustedScreenTabs.find((tab) => tab.isActive);
		if (activeAdjustedTab) {
			return tabs.findIndex((tab) => tab.id === activeAdjustedTab.id);
		}
	}, [adjustmentsAreEnabled, adjustedScreenTabs, formState, tabs, selected]);

	// Controlled onChange is propagating user changing tabs to the UIM store
	const controlledOnChange = useCallback(
		(...args: Parameters<NonNullable<TabsProps['onChange']>>) => {
			const [userNewSelected] = args;

			if (adjustmentsAreEnabled) {
				updateActiveScreenTab({ selected: userNewSelected });
			}

			onChange?.(...args);
		},
		[adjustmentsAreEnabled, onChange, updateActiveScreenTab],
	);

	return {
		controlledTabs,
		controlledSelected,
		controlledOnChange,
	};
};
